import { useEffect, useState, FormEvent } from "react"
import apiClient from "../../lib/api"
import type { IHeroSlide } from "@dc-ireland/shared"
import { MAX_HERO_SLIDES } from "@dc-ireland/shared"
import ImagePicker from "../../components/admin/ImagePicker"
import AdminAlerts from "../../components/admin/AdminAlerts"

const emptySlide = (index: number): IHeroSlide => ({
  slug: `slide-${index}`,
  src: "",
  label: "New slide",
  caption: "",
  enabled: true,
})

const AdminHeroSlides = () => {
  const [slides, setSlides] = useState<IHeroSlide[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const data = await apiClient.getHeroSlides(true)
        setSlides(data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)))
      } catch (err) {
        console.error(err)
        setError("Failed to load hero slides")
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const updateSlide = (index: number, patch: Partial<IHeroSlide>) => {
    setSlides((current) =>
      current.map((slide, slideIndex) => (slideIndex === index ? { ...slide, ...patch } : slide))
    )
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const payload = slides.map((slide, index) => ({
        ...slide,
        slug: slide.slug || `slide-${index}`,
        sortOrder: index,
      }))
      const updated = await apiClient.updateHeroSlides(payload)
      setSlides(updated.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)))
      setSuccess("Hero slideshow updated.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update hero slides")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <span className="loading loading-spinner loading-md"></span>
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Hero Slideshow</h2>
        <p className="mt-1 text-secondary">
          Slides display in list order on the landing page. You can add up to {MAX_HERO_SLIDES} slides.
        </p>
      </div>

      <AdminAlerts
        error={error}
        success={success}
        onClearError={() => setError(null)}
        onClearSuccess={() => setSuccess(null)}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        {slides.map((slide, index) => (
          <div key={slide.id ?? `slide-${index}`} className="card bg-base-100 shadow-xl">
            <div className="card-body gap-4 lg:flex-row">
              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                <label className="form-control">
                  <span className="label-text">Label</span>
                  <input
                    className="input input-bordered input-sm"
                    value={slide.label}
                    onChange={(e) => updateSlide(index, { label: e.target.value })}
                    required
                  />
                </label>
                <ImagePicker
                  label="Image"
                  value={slide.src}
                  onChange={(src) => updateSlide(index, { src })}
                  required
                />
                <label className="form-control sm:col-span-2">
                  <span className="label-text">Caption</span>
                  <input
                    className="input input-bordered input-sm"
                    value={slide.caption}
                    onChange={(e) => updateSlide(index, { caption: e.target.value })}
                    required
                  />
                </label>
                <label className="label cursor-pointer justify-start gap-3 sm:col-span-2">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    checked={slide.enabled !== false}
                    onChange={(e) => updateSlide(index, { enabled: e.target.checked })}
                  />
                  <span className="label-text">Show on landing page</span>
                </label>
              </div>

              <div className="flex shrink-0 flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-secondary">
                  Slide {index + 1}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={index === 0}
                  onClick={() => {
                    setSlides((current) => {
                      const next = [...current]
                      ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
                      return next
                    })
                  }}
                >
                  Move up
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={index === slides.length - 1}
                  onClick={() => {
                    setSlides((current) => {
                      const next = [...current]
                      ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
                      return next
                    })
                  }}
                >
                  Move down
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm text-error"
                  disabled={slides.length <= 1}
                  onClick={() => {
                    setSlides((current) => current.filter((_, slideIndex) => slideIndex !== index))
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={slides.length >= MAX_HERO_SLIDES}
            onClick={() => setSlides((current) => [...current, emptySlide(current.length)])}
          >
            Add slide ({slides.length}/{MAX_HERO_SLIDES})
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving || slides.length === 0}>
            {saving ? "Saving…" : "Save Slideshow"}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdminHeroSlides
